import { useEffect, useRef, useState } from 'react'
import { ChevronDown, Check, Loader2, AlertTriangle } from 'lucide-react'
import { postJson, apiGet } from '../lib/api.js'
import { useTypeahead } from '../lib/useTypeahead.js'
import { STATUS_GROUPS, PATIENT_STATUSES, STATUS_TONES, statusTone } from '../data/patientStatuses.js'

// Pull a human message out of postJson's "HTTP 400: {json}" error string.
function niceError(e) {
  let m = e?.message || 'เปลี่ยนสถานะไม่สำเร็จ'
  const i = m.indexOf('{')
  if (i >= 0) { try { const o = JSON.parse(m.slice(i)); m = o.message || o.error || m } catch { /* keep */ } }
  return m
}

/**
 * Patient status pill + grouped, color-coded dropdown. Picking a status POSTs to
 * /api/patients/status/{id}. On failure we show an alert popup and, once it's
 * closed, re-read the patient (the server may have flipped the status to Error).
 */
export default function StatusControl({ patientId, status, onChanged, disabled, align = 'left', compact = false }) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [saving, setSaving] = useState(false)
  const [errMsg, setErrMsg] = useState(null)
  const ref = useRef(null)

  useEffect(() => {
    function onDoc(e) { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setQuery('') } }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [])

  async function pick(s) {
    if (s === status) { setOpen(false); setQuery(''); return }
    setSaving(true)
    try {
      await postJson(`/api/patients/status/${patientId}`, { status: s })
      onChanged?.(s)
      setOpen(false); setQuery('')
    } catch (e) {
      setOpen(false); setQuery('')
      setErrMsg(niceError(e))
    } finally {
      setSaving(false)
    }
  }

  // The change may have failed AND moved the status server-side (e.g. → Error).
  // Re-read the authoritative value before dismissing the alert.
  async function closeErr() {
    try {
      const p = await apiGet(`/api/patients/${patientId}`)
      if (p && 'status' in p) onChanged?.(p.status || '')
    } catch { /* ignore */ }
    setErrMsg(null)
  }

  const tone = STATUS_TONES[statusTone(status)] || STATUS_TONES.none
  // `compact` matches the read-only status pill in the huddle queue rows, so
  // making that pill editable doesn't change the density of the list.
  const trigger = compact
    ? 'gap-1 px-1.5 py-0.5 text-[10.5px] font-medium'
    : 'gap-1.5 px-2.5 py-1 text-[13px] font-semibold'
  const icon = compact ? 10 : 12
  const q = query.trim().toLowerCase()
  const flat = q ? PATIENT_STATUSES.filter((s) => s.toLowerCase().includes(q)) : null

  // Flat, index-aligned list the keyboard nav walks: the filtered hits when
  // searching, otherwise every status in grouped render order (PATIENT_STATUSES
  // is STATUS_GROUPS flattened, so indices line up with the rendered rows).
  const navItems = flat ?? PATIENT_STATUSES
  const ta = useTypeahead({
    count: navItems.length,
    onSelect: (i) => pick(navItems[i]),
    onClose: () => { setOpen(false); setQuery('') },
  })

  const Row = (s) => {
    const t = STATUS_TONES[statusTone(s)] || STATUS_TONES.none
    const sel = s === status
    const i = navItems.indexOf(s)
    const active = i === ta.activeIndex
    return (
      <button
        key={s}
        type="button"
        data-ti-index={i}
        onMouseEnter={() => ta.setActiveIndex(i)}
        onClick={() => pick(s)}
        className={`flex w-full items-center gap-2.5 text-left px-3 py-2 text-[14px] transition-colors hover:bg-slate-50 ${sel ? 'bg-slate-50 font-medium' : ''} ${active ? 'bg-slate-50' : ''}`}
      >
        <span className={`w-2 h-2 rounded-full flex-none ring-1 ring-black/5 ${t.dot}`} />
        <span className="flex-1 truncate text-slate-700">{s}</span>
        {sel && <Check size={13} strokeWidth={3} className="text-brand-600 flex-none" />}
      </button>
    )
  }

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        disabled={disabled || saving}
        onClick={() => setOpen((o) => !o)}
        title={status || 'ตั้งสถานะ'}
        className={`inline-flex items-center rounded-full ring-1 transition-colors disabled:opacity-60 whitespace-nowrap ${trigger} ${tone.pill}`}
      >
        {saving
          ? <Loader2 size={icon} className="animate-spin flex-none" />
          : <span className={`w-1.5 h-1.5 rounded-full flex-none ${tone.dot}`} />}
        <span className={`truncate ${compact ? 'max-w-[104px]' : 'max-w-[150px]'}`}>{status || 'ตั้งสถานะ'}</span>
        {!disabled && <ChevronDown size={icon} className="opacity-70 flex-none" />}
      </button>

      {open && !disabled && (
        <div ref={ta.listRef} className={`absolute z-40 mt-1.5 w-64 max-h-80 overflow-auto rounded-xl border border-slate-200 bg-white shadow-xl shadow-slate-900/10 py-1 ${align === 'right' ? 'right-0' : 'left-0'}`}>
          <div className="px-2 pt-0.5 pb-1.5 sticky top-0 bg-white">
            <input
              autoFocus value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={ta.onKeyDown} placeholder="ค้นหาสถานะ…"
              className="w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-[14px] outline-none focus:ring-2 focus:ring-brand-200 focus:border-brand-300"
            />
          </div>
          {flat
            ? (flat.length ? flat.map(Row) : <div className="px-3 py-2 text-[14px] text-slate-400">ไม่พบสถานะ</div>)
            : STATUS_GROUPS.map((g) => (
                <div key={g.label}>
                  <div className="px-3 pt-2 pb-1 text-[12px] font-semibold uppercase tracking-wide text-slate-400">{g.label}</div>
                  {g.items.map(Row)}
                </div>
              ))}
        </div>
      )}

      {errMsg && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-slate-900/30 backdrop-blur-[2px]" onMouseDown={closeErr} />
          <div className="relative w-full max-w-sm bg-white rounded-2xl shadow-2xl shadow-slate-900/20 ring-1 ring-slate-200 p-5">
            <div className="flex items-center gap-3 mb-2.5">
              <span className="w-9 h-9 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center ring-1 ring-rose-200 flex-none"><AlertTriangle size={16} strokeWidth={2.25} /></span>
              <div className="text-[15.5px] font-semibold text-slate-900">เปลี่ยนสถานะไม่สำเร็จ</div>
            </div>
            <div className="text-[14px] text-slate-600 leading-relaxed mb-4 break-words">{errMsg}</div>
            <div className="flex justify-end">
              <button type="button" onClick={closeErr} className="px-3.5 py-1.5 rounded-lg text-[14px] font-semibold bg-brand-600 hover:bg-brand-700 text-white">ปิด</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
